import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Lock as LockIcon } from "@mui/icons-material";

/**
 * component that prompts unauthenticated users to login, sign up, or upgrade.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Handler for closing the dialog
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {boolean} [props.requireMembership] - Whether the feature requires a membership
 * @param {boolean} [props.hasMembership] - Whether the user has a membership
 * @param {string} [props.featureName] - Name of the feature being restricted
 * @returns {JSX.Element}
 * @component
 */
const UpsellOrLogin = ({
  open,
  onClose,
  isAuthenticated,
  requireMembership = false,
  hasMembership = false,
  featureName = "feature",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const currentPath = encodeURIComponent(location.pathname + location.search);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="upsell-dialog-title"
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            px: { xs: 1, md: 2 },
          },
        },
      }}
    >
      <DialogTitle id="upsell-dialog-title" sx={{ textAlign: "center", pt: 3 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              borderRadius: "50%",
              p: 1,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 50,
              height: 50,
            }}
          >
            <LockIcon fontSize="large" />
          </Box>
          <Typography variant="h5" component="span" fontWeight="bold">
            Members-Only {featureName}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" align="center" component="p" sx={{ pt: 1 }}>
          {isAuthenticated
            ? "Your current membership doesn't provide access to this feature. Please upgrade your subscription to access exclusive content."
            : "Please log in or sign up to access this exclusive content and all member benefits."}
        </Typography>

        <Box
          sx={{
            bgcolor: "action.hover",
            p: 2,
            borderRadius: 1,
            mt: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {isAuthenticated
              ? "As a paid member you'll get access to all restricted features including member-only sessions, private networking, and educational resources."
              : "SMFA members enjoy exclusive benefits including access to all features.  Join us today to unlock these benefits!"}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: "center", pb: 3, gap: 1, flexWrap: "wrap" }}
      >
        <Button onClick={onClose} color="inherit" variant="outlined">
          Close
        </Button>

        <Button
          component={RouterLink}
          to={isAuthenticated ? "/dashboard" : "/signup"}
          color="primary"
          variant="contained"
        >
          {isAuthenticated ? "Upgrade Membership" : "Sign Up"}
        </Button>

        {!isAuthenticated && (
          <Button
            component={RouterLink}
            to={`/login?redirect=${currentPath}`}
            color="secondary"
            variant="contained"
          >
            Log In
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpsellOrLogin;
